'use client';

import { MDSTextArea } from '../../Forms/TextArea/TextArea';
import {
  ChangeEventHandler,
  ComponentProps,
  FocusEventHandler,
  useCallback,
} from 'react';
import { UseControllerProps, useController } from 'react-hook-form';

type Props = Omit<
  ComponentProps<typeof MDSTextArea>,
  'invalid' | 'errorText' | 'value'
> &
  Pick<UseControllerProps, 'rules'>;

/**
 ```jsx
 import { MDSTextAreaForm } from '@midwest/web/forms';
 ```
 * Wrapper around [TextArea](/story/inputs-controls-text-area--docs) to connect component to react-hook-form controlled form.
 *
 * Use `defaultValues` and `setValue` from react-hook-form to set the value of the text area.
 */
export const MDSTextAreaForm = ({
  name,
  rules,
  onChange,
  onBlur,
  ...props
}: Props) => {
  const {
    field: { onChange: formOnChange, onBlur: formOnBlur, value, ref },
    fieldState: { error },
  } = useController({ name, rules });
  const invalid = !!error;

  const handleChange: ChangeEventHandler = useCallback(
    (e) => {
      formOnChange(e);
      onChange && onChange(e);
    },
    [formOnChange, onChange],
  );

  const handleBlur: FocusEventHandler = useCallback(
    (e) => {
      formOnBlur();
      onBlur && onBlur(e);
    },
    [formOnBlur, onBlur],
  );

  return (
    <MDSTextArea
      {...props}
      name={name}
      invalid={invalid}
      errorText={error?.message}
      onChange={handleChange}
      onBlur={handleBlur}
      value={value}
      ref={ref}
    />
  );
};

MDSTextAreaForm.displayName = 'MDSTextAreaForm';
